/**
 * blocky-animal.js
 * Lớp khởi tạo mô hình 3D động vật hình hộp (Blocky / Voxel Animal)
 * Tái sử dụng tối đa qua 02 Khung cơ sở tham số (Quadruped & Avian)
 * Kết hợp Procedural Canvas Texture động và bộ diễn hoạt sinh học AnimalAnimator.
 */

import * as THREE from 'three';
import { ANIMAL_PRESETS } from './animal-presets.js';
import { AnimalAnimator } from './animal-animator.js';
import { createSimpleBlobShadow, enforceShadowPolicy } from '../core/blob-shadow.js';
import { disposeObject3D } from '../core/dispose.js';
import { buildQuadruped } from './animals/build-quadruped.js';
import { buildAvian } from './animals/build-avian.js';

export const QUADRUPED_TAIL_TYPES = ['cat_L', 'wagging_up', 'pig_curly', 'hanging_tuft', 'horse_bushy', 'short_stub'];

export const EAR_TYPES = ['pointy', 'pointy_tall', 'side', 'side_droop', 'floppy', 'droop', 'floppy_front'];

export class BlockyAnimal {
  /**
   * @param {string} speciesKey - 'DOG' | 'CAT' | 'COW' | 'PIG' | 'SHEEP' | 'HORSE' | 'CHICKEN' | 'DUCK'
   * @param {string} variantKey - Khóa biến thể màu lông/vân
   * @param {object} customOptions - Tùy chọn kích thước/màu sắc bổ sung
   */
  constructor(speciesKey = 'DOG', variantKey = null, customOptions = {}) {
    this.speciesKey = speciesKey.toUpperCase();
    this.preset = ANIMAL_PRESETS[this.speciesKey] || ANIMAL_PRESETS.DOG;
    this.currentVariantKey = variantKey || this.preset.defaultVariant;
    this.variant = this.preset.variants[this.currentVariantKey] || Object.values(this.preset.variants)[0];
    this.customOptions = customOptions;
    this.userScale = customOptions.scale || 1.0;

    this.root = new THREE.Group();
    this.root.name = `Animal_${this.speciesKey}_${this.currentVariantKey}`;

    this.parts = {};
    this.disposables = [];

    // Xây dựng cây phân cấp 3D
    this.buildHierarchy();

    // Khởi tạo bộ diễn hoạt sinh học với legHeight chuẩn và độ dày len
    this.animator = new AnimalAnimator(this.parts, this.preset.archetype, {
      legHeight: this.preset.morph.legs?.height || 0.16,
      woolThickness: this.preset.morph.hasWoolLayer ? this.preset.morph.woolThickness || 0.04 : 0
    });

    // Tạo bóng mờ mềm mại (Blob Shadow) dưới đất với tỉ lệ theo kích thước thân
    this.createBlobShadow();

    // Thiết lập bóng chuẩn Studio
    this.enforceShadowPolicy();
  }

  buildHierarchy() {
    const morph = this.preset.morph;
    const baseScale = (morph.scale || 1.0) * this.userScale;
    this.root.scale.set(baseScale, baseScale, baseScale);

    if (this.preset.archetype === 'avian') {
      buildAvian(this.root, morph, this.variant, this.parts, this.disposables);
    } else {
      buildQuadruped(this.root, morph, this.variant, this.parts, this.disposables);
    }
  }

  createBlobShadow() {
    const m = this.preset.morph;
    // Bán kính bóng tỉ lệ thuận theo kích thước thân thú (P-7)
    const radius = Math.max(m.body.length || 0.6, m.body.width || 0.4) * 0.75;
    const { mesh, geometry, material } = createSimpleBlobShadow({ radius, opacity: 0.35 });
    this.root.add(mesh);
    this.blobShadow = mesh;
    this.disposables.push(geometry, material);
  }

  enforceShadowPolicy() {
    enforceShadowPolicy(this.root, { blobShadow: this.blobShadow });
  }

  playAction(actionName) {
    if (this.animator) {
      this.animator.play(actionName);
    }
  }

  update(delta) {
    if (this.animator) {
      this.animator.update(delta);
    }
  }

  setVariant(variantKey) {
    if (!this.preset.variants[variantKey]) return;
    this.currentVariantKey = variantKey;
    this.variant = this.preset.variants[variantKey];

    // Xóa mesh cũ và tạo lại theo biến thể mới
    this.dispose();
    this.root.clear();
    this.parts = {};
    this.buildHierarchy();
    this.animator = new AnimalAnimator(this.parts, this.preset.archetype, {
      legHeight: this.preset.morph.legs?.height || 0.16,
      woolThickness: this.preset.morph.hasWoolLayer ? this.preset.morph.woolThickness || 0.04 : 0
    });
    this.createBlobShadow();
    this.enforceShadowPolicy();
  }

  setScale(scaleFactor) {
    this.userScale = scaleFactor;
    const baseScale = this.preset.morph.scale || 1.0;
    const finalScale = baseScale * scaleFactor;
    this.root.scale.setScalar(finalScale);
  }

  dispose() {
    disposeObject3D(this.root, { disposeTextures: true });
    this.disposables.forEach((item) => {
      if (item && typeof item.dispose === 'function') {
        item.dispose();
      }
    });
    this.disposables = [];
  }
}
